import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import keycloak from './keycloak';
import type Keycloak from 'keycloak-js';

import { ThemeProvider } from './providers/ThemeProvider';
import { ToastProvider } from './providers/ToastProvider';
import { ConfirmProvider } from './providers/ConfirmProvider';
import { PromptProvider } from './providers/PromptProvider';
import { FeaturesProvider } from './providers/FeaturesProvider';
import { PageLoader } from './components/ui/skeleton';

import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';

import Login from './pages/Login';
import Profile from './pages/Profile';
import Services from './pages/Services';
import Domains from './pages/Domains';
import Websites from './pages/Websites';
import WebsiteFiles from './pages/WebsiteFiles';
import CodeEditor from './pages/CodeEditor';
import Containers from './pages/Containers';
import AdminDomains from './pages/AdminDomains';
import AdminVps from './pages/AdminVps';
import AdminDatabases from './pages/AdminDatabases';
import AdminChangelog from './pages/AdminChangelog';
import AdminUsers from './pages/AdminUsers';
import Vps from './pages/Vps';
import Orders from './pages/Orders';
import AdminOrders from './pages/AdminOrders';
import Emails from './pages/Emails';
import Emailing from './pages/Emailing';
import Databases from './pages/Databases';
import Messages from './pages/Messages';
import WhatsApp from './pages/WhatsApp';
import Bills from './pages/Bills';
import AdminBills from './pages/AdminBills';
import Support from './pages/Support';
import AdminSupport from './pages/AdminSupport';
import AdminServices from './pages/AdminServices';
import Status from './pages/Status';

export interface KeycloakContextType {
  keycloak: Keycloak;
  authenticated: boolean;
}

export const KeycloakContext = createContext<KeycloakContextType | null>(null);

export function useKeycloak(): KeycloakContextType {
  const ctx = useContext(KeycloakContext);
  if (!ctx) throw new Error('useKeycloak must be used within KeycloakContext.Provider');
  return ctx;
}

function SplashScreen() {
  return (
    <PageLoader fullScreen className="bg-background" label="Loading dashboard..." />
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const initAttempted = React.useRef<boolean>(false);

  useEffect(() => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    const buildRedirectUri = (): string => {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('session_state');
      url.searchParams.delete('iss');
      return url.toString();
    };

    const initOptions = {
      onLoad: 'check-sso' as const,
      checkLoginIframe: false,
      pkceMethod: 'S256' as const,
      redirectUri: buildRedirectUri(),
    };

    keycloak
      .init(initOptions)
      .then((auth) => {
        setAuthenticated(auth);
        setInitialized(true);
        if (auth) {
          const interval = setInterval(() => {
            keycloak.updateToken(70).catch(() => clearInterval(interval));
          }, 60_000);
        }
      })
      .catch(() => {
        setAuthenticated(false);
        setInitialized(true);
      });
  }, []);

  if (!initialized) {
    return (
      <ThemeProvider>
        <SplashScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <ConfirmProvider>
          <PromptProvider>
            <KeycloakContext.Provider value={{ keycloak, authenticated }}>
              <FeaturesProvider>
              <Router>
                {!authenticated ? (
                  <Routes>
                    <Route path="*" element={<Login />} />
                  </Routes>
                ) : (
                  <Routes>
                    <Route path="/" element={<DashboardLayout />}>
                      <Route index element={<Navigate to="/services" replace />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="services" element={<Services />} />
                      <Route path="domains" element={<Domains />} />
                      <Route path="websites" element={<Websites />} />
                      <Route path="websites/:websiteId/files" element={<WebsiteFiles />} />
                      <Route path="websites/:websiteId/editor/:filePath" element={<CodeEditor />} />
                      <Route path="containers" element={<Containers />} />
                      <Route path="vps" element={<Vps />} />
                      <Route path="emails" element={<Emails />} />
                      <Route path="emailing" element={<Emailing />} />
                      <Route path="databases" element={<Databases />} />
                      <Route path="messages" element={<Messages />} />
                      <Route path="whatsapp" element={<WhatsApp />} />
                      <Route path="bills" element={<Bills />} />
                      <Route path="orders" element={<Orders />} />
                      <Route path="support" element={<Support />} />
                      <Route path="status" element={<Status />} />
                    </Route>
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<Navigate to="/admin/services" replace />} />
                      <Route path="domains" element={<AdminDomains />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="vps" element={<AdminVps />} />
                      <Route path="databases" element={<AdminDatabases />} />
                      <Route path="changelog" element={<AdminChangelog />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="bills" element={<AdminBills />} />
                      <Route path="support" element={<AdminSupport />} />
                      <Route path="services" element={<AdminServices />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                )}
              </Router>
              </FeaturesProvider>
            </KeycloakContext.Provider>
          </PromptProvider>
        </ConfirmProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
